import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Menu } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconCheck, IconPalette } from "@tabler/icons-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { withViewTransition } from "../../hooks/useViewTransition"
import { updateLexThemePreference } from "../../lib/api/lex-theme"
import {
  applyLexTheme,
  LEX_THEME_KEYS,
  LEX_THEME_LABELS,
  readActiveLexTheme,
  type TLexThemeKey,
} from "../../lib/lex-themes"
import { queryKeys } from "../../lib/query-keys"
import type { IProps } from "./interfaces"

/**
 * Lexicanon theme picker for the user dropdown. Smart: it persists the choice
 * to the user's account (so it follows them across devices) and applies it to
 * the live document optimistically. Mirrors VoxcanonThemeToggle's mutation +
 * document-sync pattern. Renders a nested Menu.Sub — a paint-palette
 * "Theme" entry that expands to the theme list — so it slots into the
 * existing user Menu.Dropdown without taking over the whole menu.
 */
export const LexThemeMenu = ({ current }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const queryClient = useQueryClient()
  const [active, setActive] = useState<TLexThemeKey>(current ?? readActiveLexTheme())

  // Keep the checkmark in sync if the theme changes elsewhere (post-auth sync,
  // a ?theme= preview, another tab) without re-controlling via prop.
  useEffect(() => {
    if (typeof document === "undefined") return
    const root = document.documentElement
    const sync = (): void => setActive((prev) => (prev === readActiveLexTheme() ? prev : readActiveLexTheme()))
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] })
    return () => observer.disconnect()
  }, [])

  const mutation = useMutation({
    mutationFn: (theme: TLexThemeKey) => updateLexThemePreference(theme),
    onError: () => {
      notifications.show({
        color: "danger",
        title: i18n._(msg`Could not save theme`),
        message: i18n._(msg`Your choice will only apply on this browser until we can reach the server.`),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.session() })
    },
  })

  const handleSelect = useCallback(
    (theme: TLexThemeKey): void => {
      withViewTransition(() => {
        applyLexTheme(theme)
      })
      setActive(theme)
      mutation.mutate(theme)
    },
    [mutation],
  )

  return (
    <Menu.Sub>
      <Menu.Sub.Target>
        <Menu.Sub.Item leftSection={<IconPalette size={14} />}>{i18n._(msg`Palette`)}</Menu.Sub.Item>
      </Menu.Sub.Target>
      <Menu.Sub.Dropdown>
        {LEX_THEME_KEYS.map((key) => (
          <Menu.Item
            key={key}
            closeMenuOnClick={false}
            onClick={() => handleSelect(key)}
            rightSection={active === key ? <IconCheck size={14} /> : null}
          >
            {LEX_THEME_LABELS[key]}
          </Menu.Item>
        ))}
      </Menu.Sub.Dropdown>
    </Menu.Sub>
  )
}
